import moment from 'moment/moment';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gender } from '../types';

interface SearchParams {
  date: string,
  customFilter: string,
  calledBy: string,
  showAll: string,
  gender: string,
}

const useRouteChanges = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    date: '',
    customFilter: '',
    calledBy: '',
    showAll: '',
    gender: '',
  });

  const date = searchParams.get('date') ? moment(searchParams.get('date')) : undefined;
  const calledBy = searchParams.get('calledBy') || '';
  const showAll = searchParams.get('showAll') || '';
  const gender = searchParams.get('gender') ? searchParams.get('gender') as Gender : null;

  const lastSearchParamsRef = useRef<SearchParams>({
    date: searchParams.get('date') || '',
    calledBy: searchParams.get('calledBy') || '',
    showAll: searchParams.get('showAll') || '',
    gender: searchParams.get('gender') || '',
    customFilter: searchParams.get('customFilter') || '',
  });

  // keep the snapshot current when the url changes underneath us (browser back/forward)
  useEffect(() => {
    lastSearchParamsRef.current = {
      date: searchParams.get('date') || '',
      calledBy: searchParams.get('calledBy') || '',
      showAll: searchParams.get('showAll') || '',
      gender: searchParams.get('gender') || '',
      customFilter: searchParams.get('customFilter') || '',
    };
  }, [searchParams]);

  const partialUpdateSearchParams = useCallback((nextSearchParams: Partial<SearchParams>) => {
    lastSearchParamsRef.current = {
      ...lastSearchParamsRef.current, // use ref so this can be called quickly and not miss data
      ...nextSearchParams,
    };
    setSearchParams(Object.fromEntries(
      Object.entries(lastSearchParamsRef.current).filter(([_, value]) => !!value)
    ));
  }, [setSearchParams]);

  const setDate = useCallback((value: Date | null) => {
    partialUpdateSearchParams({
      date: value ? moment(value).format('YYYY-MM-DD') : '',
      calledBy: '',
    });
  }, [partialUpdateSearchParams]);

  const setGender = useCallback((value: Gender | null) => {
    partialUpdateSearchParams({
      gender: value || '',
      // women only appear in the wider position list
      ...(value === Gender.FEMALE ? { showAll: 'true' } : {}),
    });
  }, [partialUpdateSearchParams]);

  const setCalledBy = useCallback((calledBy: string) => {
    partialUpdateSearchParams({ calledBy });
  }, [partialUpdateSearchParams]);

  const toggleShowAll = useCallback(() => {
    partialUpdateSearchParams({ showAll: showAll === 'true' ? '' : 'true' });
  }, [partialUpdateSearchParams, showAll]);

  return {
    calledBy,
    date,
    gender,
    partialUpdateSearchParams,
    searchParams,
    setCalledBy,
    setDate,
    setGender,
    showAll,
    toggleShowAll,
  };
};

type RouteChanges = ReturnType<typeof useRouteChanges>;

const RouteChangesContext = createContext<RouteChanges | null>(null);

// all route state lives in this single provider instance so updates from any
// component merge against the same snapshot
export const RouteChangesProvider = ({ children }: { children: ReactNode }) => (
  <RouteChangesContext.Provider value={useRouteChanges()}>
    {children}
  </RouteChangesContext.Provider>
);

const useRouteChangesContext = (): RouteChanges => {
  const context = useContext(RouteChangesContext);
  if (!context) {
    throw new Error('useRouteChangesContext must be used inside RouteChangesProvider');
  }
  return context;
};

export default useRouteChangesContext;
